import React, { useContext } from "react";

import gql from "graphql-tag";
import { useMutation } from "@apollo/react-hooks";
import { OrganizationContext } from "../../../contexts/OrganizationProvider";
import { Heading, Text } from "../../Type";
import { useModalContext } from "../../../contexts/ModalProvider";
import FileDetails from "../FileDetails";
import Modal from "../../Modal";
import { Secondary, Primary } from "../../Button";
import { TextInput } from "../../Inputs";
import Alert from "../../Alert";
import { uuid } from "../../../helpers/reports";
import styles from "./index.module.scss";
import { logErrorToLogdna } from "../../../helpers/logdna";
import { ApolloError } from "apollo-boost";

const ERROR_CODES = {
  updatingReport: "UPDATING_REPORT"
};

const ALLOWED_TYPES = {
  gif: "image/gif",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  mpg: "video/mpeg",
  mpeg: "video/mpeg",
  mp3: "audio/mpeg",
  pdf: "application/pdf",
  png: "image/png",
  txt: "text/plain"
};
const ALLOWED_MIME_TYPES = Object.values(ALLOWED_TYPES);
const ALLOWED_EXTENSIONS = Object.keys(ALLOWED_TYPES).map(ext => `.${ext}`);

const MAX_FILE_SIZE_MB = 10;
// eslint-disable-next-line no-magic-numbers
const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024;

const UPLOAD_REPORT = gql`
  mutation uploadReport(
    $name: String!
    $title: String!
    $organizationId: String!
    $contentType: String!
    $sizeInByte: Int!
  ) {
    uploadReport(
      name: $name
      title: $title
      organizationId: $organizationId
      contentType: $contentType
      sizeInByte: $sizeInByte
    ) {
      url
    }
  }
`;

type PropsType = {
  onSuccess: () => void;
};

const UploadModal = ({ onSuccess }: PropsType) => {
  const [uploadReport] = useMutation(UPLOAD_REPORT, {
    refetchQueries: ["reports"],
    onError: (err: ApolloError) => {
      logErrorToLogdna(
        ERROR_CODES.updatingReport,
        "Apollo error while updating report.",
        err
      );
      throw err;
    }
  });

  const { setOpenModal, openModal } = useModalContext();
  const [fileTitles, setFileTitles] = React.useState<Array<string>>([]);
  const [uploading, setUploading] = React.useState(false);
  const [success, setSuccess] = React.useState(false);
  const [filesToUpload, setFilesToUpload] = React.useState<Array<File>>([]);
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const { activeOrg } = useContext(OrganizationContext);

  if (!activeOrg) {
    return null;
  }

  const onFileSelectHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList) return;

    const files = Array.from(fileList);

    const invalidType = files.find(
      file => !ALLOWED_MIME_TYPES.includes(file.type)
    );
    if (invalidType) {
      setFilesToUpload([]);
      setFileTitles([]);
      setErrorMessage(
        `File type not allowed. ${invalidType.name} is not supported.`
      );
      return;
    }

    const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE);
    if (oversizedFile) {
      setFilesToUpload([]);
      setFileTitles([]);
      setErrorMessage(`Each file must be smaller than ${MAX_FILE_SIZE_MB} MB.`);
      return;
    }

    setErrorMessage("");
    setFilesToUpload(files);
    setFileTitles(files.map(f => f.name));
  };

  const onTitleChange = (idx: number, value: string) => {
    setFileTitles(titles => {
      const newTitles = [...titles];
      newTitles[idx] = value;
      return newTitles;
    });
  };

  const uploadFileHandler = async () => {
    if (!filesToUpload.length) return;
    setUploading(true);
    setError(false);

    try {
      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i];
        const name = `${uuid()}-${file.name.replace(/\s/g, "-")}`;
        const variables = {
          name,
          contentType: file.type,
          organizationId: activeOrg.id,
          title: fileTitles[i] || file.name,
          sizeInByte: file.size
        };
        const options = {
          method: "PUT",
          body: file
        };
        const result = await uploadReport({ variables });
        if (result.data) {
          const response = await fetch(result.data.uploadReport.url, options);
          if (!response.ok) setError(true);
        }
      }
      setSuccess(true);
      onSuccess();
    } catch (err) {
      setError(true);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal
      className={styles.UploadModal}
      isOpen={openModal === "upload-report"}
    >
      <div>
        <Heading size="h6" className={styles.ModalTitle}>
          Upload Report(s)
        </Heading>
        {success && filesToUpload.length ? (
          <div className={styles.Title}>
            {filesToUpload.map((file, idx) => (
              <FileDetails
                key={`${file.name}${idx}`}
                file={{
                  title: fileTitles[idx],
                  sizeInByte: file.size,
                  contentType: file.type
                }}
              />
            ))}
          </div>
        ) : (
          <div>
            <div className={styles.File}>
              <Text className={styles.FileText}>File(s)</Text>
              <input
                className={styles.FileInput}
                type="file"
                multiple
                accept={ALLOWED_EXTENSIONS.join(",")}
                onChange={onFileSelectHandler}
              />
            </div>
            {error || errorMessage ? (
              <Alert
                message={errorMessage || "Failed to upload file(s). Try again."}
                type="error"
              />
            ) : null}
            <div className={styles.Title}>
              {filesToUpload.map((file, idx) => (
                <TextInput
                  key={`${file.name}${idx}`}
                  label={`Title for ${file.name} below:`}
                  placeholder="Add a title"
                  value={fileTitles[idx] || ""}
                  onChange={val => onTitleChange(idx, val)}
                  disabled={success}
                />
              ))}
            </div>
            <Text>
              {uploading ? "Uploading report(s), please wait..." : ""}
            </Text>
          </div>
        )}
        <div className={styles.Buttons}>
          <Secondary
            buttonSize="small"
            className={styles.Button}
            onClick={() => {
              setSuccess(false);
              setOpenModal("");
              setFileTitles([]);
              setFilesToUpload([]);
            }}
            disabled={uploading}
          >
            {success ? "Close" : "Cancel"}
          </Secondary>
          {!success ? (
            <Primary
              buttonSize="small"
              className={styles.Button}
              onClick={uploadFileHandler}
              disabled={uploading || success || !filesToUpload.length}
            >
              Upload
            </Primary>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default UploadModal;
